/* eslint-disable react-hooks/exhaustive-deps */
import Bugsnag from '@bugsnag/js'
import { useMutation } from 'react-query'
import { React, useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Icon, InputAdornment, Typography, CardMedia, CircularProgress } from '@mui/material'
import EDDrawer from './EDDrawer'
import AdxInput from 'components/custom/AdxInput'
import defaultImage from 'assets/images/default_avatar.png'
import AdxPagination from 'components/custom/AdxPagination'
import PerfSortTable from 'components/reusable/table/PerfSortTable'
import { getEvaluationCSV } from 'services/api/modules/performance/appraisal/result'
import { confirmUploadMatching } from 'services/api/modules/performance/appraisal/appraisal'
import { getEvalParticipants } from 'services/api/modules/performance/appraisal/participants'
import UploadMatchingCsv from '../../appraisal-cycle-config/view/UploadMatchingCsv'
import ACMatchingTable from '../../appraisal-cycle-config/components/ACMatchingTable'
import AdxButton from 'components/custom/AdxButton'
import { downloadResponseFile } from 'services/helper/fileDownload'

const EDSortingTable = ({ title }) => {
  const { pathname } = useLocation()
  const inputEl = useRef('')
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [getMatchingData, setGetMatchingData] = useState('')
  const [showMatchingTable, setShowMatchingTable] = useState(false)
  const [completeUploadMatching, setCompleteUploadMatching] = useState(false)
  const [staffData, setStaffData] = useState([])
  const [upload, setUpload] = useState(false)
  const [matchingLoading, setMatchingLoading] = useState(true)
  const [matchingCSVUpload, setMatchingCSVUpload] = useState(false)
  const evaluationId = pathname.split('/').pop()

  const {
    isLoading,
    data,
    mutate: getParticipants,
  } = useMutation((values) => getEvalParticipants(evaluationId, currentPage, values), {})

  const { mutate: downloadEvaluationResult } = useMutation(getEvaluationCSV, {
    onSuccess: (res) => {
      // let updatedTitle = title?.replaceAll(' ', '_')
      downloadResponseFile(res, {
        fileName: `${title}_result.xlsx`,
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        successMessage: 'Evaluation result downloaded.',
      })
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const { mutate: mutateConfirmUploadMatching } = useMutation(confirmUploadMatching, {
    onSuccess: async () => {
      setGetMatchingData('')
      setShowMatchingTable(false)
      setStaffData([])
      setMatchingLoading(true)
      handleCloseMatchingCSV()
      getParticipants('')
    },
  })

  const handleDownloadResult = () => {
    downloadEvaluationResult(evaluationId)
  }

  const handleUploadParticipants = () => {
    setMatchingCSVUpload(true)
  }

  const handleCloseMatchingCSV = () => {
    setCompleteUploadMatching(false)
    setMatchingCSVUpload(false)
    setUpload(false)
  }

  useEffect(() => {
    let newSearch = {}
    newSearch = {
      search: searchTerm,
    }
    setTimeout(() => {
      getParticipants(newSearch)
    }, 1000)
  }, [currentPage, searchTerm])

  const participants = data?.data

  const employeeColumn = [
    {
      Header: 'Employee',
      accessor: 'name',
      Cell: (props) => {
        const { value } = props
        return (
          <Box display="flex" alignItems="center" justifyContent="flex-start">
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                  marginRight: 2,
                }}
                src={props?.row?.original?.profile_image || defaultImage}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
              alignItems="flex-start"
            >
              <Typography>{value}</Typography>
              <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                {props?.row?.original?.designation}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Department',
      accessor: 'department_name',
    },
    {
      Header: 'Score',
      accessor: 'total_participant_score', // sorting is based on accessor value, if string then A-Z. If number small - large
      Cell: (props) => {
        const { value } = props

        return (
          <Box>
            <Typography>{value ? Math.round(value * 50) / 10 : '--'}</Typography>
          </Box>
        )
      },
    },
  ]

  const getSearchTerm = (e) => {
    setSearchTerm(e.target.value)
  }

  // Drawer function
  const [staff, setStaff] = useState([])
  const [drawerState, setDrawerState] = useState({ right: false })
  const handleDrawer = (anchor, open) => (item) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    setStaff(item)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100vh - 200px',
        rowGap: 4,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: { xs: null, sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          rowGap: 1,
          padding: 0.2,
          gap: 3,
        }}
      >
        <AdxInput
          sx={{
            width: { xs: 1, sm: 4 / 6 },
            '& .MuiInputBase-root': {
              outline: 'none',
              bgcolor: 'white.main',
            },
          }}
          value={searchTerm}
          ref={inputEl}
          onChange={getSearchTerm}
          startAdornment={
            <InputAdornment position="start">
              <Icon>search</Icon>
            </InputAdornment>
          }
          placeholder="Search employee name"
        />
        <AdxButton
          variant={'outlined'}
          onClick={handleUploadParticipants}
          sx={{ width: 'calc(100% * 1 / 5)' }}
        >
          Add participant(s)
        </AdxButton>
        <AdxButton
          variant={'outlined'}
          onClick={handleDownloadResult}
          sx={{ width: 'calc(100% * 1 / 5)' }}
        >
          Download Result
        </AdxButton>
      </Box>

      {isLoading && (
        <Box height="30vh" display="flex" justifyContent="center" alignItems="center">
          <CircularProgress color="alternate" size={60} />
        </Box>
      )}
      {!isLoading && (
        <Box sx={{ width: '100%' }}>
          <PerfSortTable
            column={employeeColumn}
            data={participants?.data ?? []}
            nav={'View'}
            onClick={handleDrawer('right', true)}
            sxNav={{ textDecoration: 'underline' }}
            sxCell
            sx={{ boxShadow: 'none' }}
            deleteUser={true}
            getParticipants={getParticipants}
          />
          {data?.data?.meta?.last_page > 1 ? (
            <Box
              width="100%"
              display="flex"
              justifyContent="center"
              py={1}
              backgroundColor="white.main"
            >
              <AdxPagination
                count={data?.data?.meta?.last_page}
                onChange={(event, page) => setCurrentPage(page)}
                defaultPage={currentPage}
              />
            </Box>
          ) : null}
        </Box>
      )}

      <EDDrawer title={title} drawerState={drawerState} handleDrawer={handleDrawer} staff={staff} />

      {matchingCSVUpload && (
        <UploadMatchingCsv
          customMatchingId={evaluationId}
          handleCloseMatchingCSV={handleCloseMatchingCSV}
          matchingCSVUpload={matchingCSVUpload}
          setShowMatchingTable={setShowMatchingTable}
          setGetMatchingData={setGetMatchingData}
          setCompleteUploadMatching={setCompleteUploadMatching}
          completeUploadMatching={completeUploadMatching}
          staffData={staffData}
          setUpload={setUpload}
          upload={upload}
          mutateConfirmUploadMatching={mutateConfirmUploadMatching}
          setMatchingLoading={setMatchingLoading}
        />
      )}
      {showMatchingTable && (
        <ACMatchingTable
          getMatchingData={getMatchingData}
          setShowMatchingTable={setShowMatchingTable}
          showMatchingTable={showMatchingTable}
          setCompleteUploadMatching={setCompleteUploadMatching}
          setStaffData={setStaffData}
          staffData={staffData}
          setUpload={setUpload}
          matchingLoading={matchingLoading}
        />
      )}
    </Box>
  )
}

export default EDSortingTable
